import * as React from 'react'
import {
  Box,
  Button,
  Typography,
  Icon,
  Paper,
  TextField,
  styled,
} from '@design/pt-ui'
import { Save as SaveIcon } from '@mui/icons-material'

import { Comment } from '../interfaces/entities/comment'
import { useDirtySanchez } from '../components/DirtySanchez'

import { StatefulModal } from './SimpleModal'
import EditButton from './EditButton'
import { translateWithUserLocale } from '../utils/i18n'
import { stripSpecialCharacters } from '../utils/string'

const CntWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'clickable',
})<{ clickable?: boolean }>(({ clickable }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  ...(clickable && { cursor: 'pointer' }),
}))

interface ICommentProps {
  title?: string
  comment: Comment | null
  editIconSize?: 'inherit' | 'default' | 'small' | 'large'
  withPaper?: boolean
  showMessageIcon?: boolean
  disabled?: boolean

  onSave?: (value: any) => void
}

interface ICommentState {
  comment: string
  dialogOpened: boolean
}

function CommentComponent(props: ICommentProps) {
  const initialComment = props.comment ? props.comment.content : ''
  const [state, setState] = React.useState<ICommentState>({
    comment: initialComment,
    dialogOpened: false,
  })

  const { onSave } = props
  const { isDirty, clean, defile } = useDirtySanchez()

  const [inputRef, setInputRef] = React.useState<HTMLInputElement | null>(null)
  React.useEffect(() => {
    if (inputRef) {
      const len = state.comment.length * 2
      inputRef.focus()
      inputRef.setSelectionRange(len, len)
      inputRef.scrollTop = 9999
    }
  }, [inputRef, state.comment])

  const handleSave = React.useCallback(() => {
    if (onSave == null) {
      return
    }

    clean('comment')
    onSave(state.comment.trim())

    setState((state) => ({
      ...state,
      dialogOpened: false,
    }))
  }, [onSave, clean, state.comment])

  const handleOnChange = React.useCallback(
    (rawValue: string) => {
      const value = stripSpecialCharacters(rawValue)

      if (value === initialComment) {
        clean('comment')
      } else {
        defile('comment')
      }

      setState((state) => ({
        ...state,
        comment: value,
      }))
    },
    [clean, defile, initialComment],
  )

  const onOpenDialog = React.useCallback(() => {
    if (!props.disabled) {
      setState((state) => ({
        ...state,
        dialogOpened: true,
      }))
    }
  }, [props.disabled])

  const onDialogClosed = React.useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      dialogOpened: false,
      comment: initialComment,
    }))
    clean('comment')
  }, [initialComment, clean])

  const content = (
    <>
      <CntWrapper
        clickable={props.onSave !== null && !props.disabled}
        onClick={onOpenDialog}
        sx={{ p: '6px 12px' }}>
        {props.title !== undefined ? (
          <Typography
            variant="subtitle1"
            align="left"
            sx={{ fontWeight: 700, mr: 0.5, fontSize: '12px' }}>
            {props.title}
          </Typography>
        ) : (
          ''
        )}
        <div>
          {props.showMessageIcon !== false ? (
            <Icon sx={{ transform: 'rotateY(180deg)' }}>message</Icon>
          ) : (
            ''
          )}
          {props.comment !== null ? (
            <Box
              component="span"
              id="cp-project-comment"
              sx={{
                ml: 0.5,
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontWeight: 400,
              }}>
              {props.comment.content}
            </Box>
          ) : null}
        </div>
        {props.onSave ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <EditButton onClick={onOpenDialog} disabled={props.disabled} />
          </Box>
        ) : null}
      </CntWrapper>
      <StatefulModal
        paperSx={{ width: '60%' }}
        open={state.dialogOpened}
        onClose={onDialogClosed}
        title={translateWithUserLocale('comment_modal.change_comment')}
        confirmClose={isDirty('comment')}>
        <Box
          sx={{
            width: '100%',
            padding: 3,
            paddingTop: 1,
            marginBottom: 4.5,
          }}>
          <TextField
            className="cp-text-field-comment-dialog"
            fullWidth={true}
            value={state.comment}
            onChange={(e) => handleOnChange(e.target.value)}
            multiline={true}
            autoFocus={false}
            inputRef={(ref) => setInputRef(ref)}
            rows={2}
            inputProps={{
              style: { boxShadow: 'none' },
            }}
            variant="standard"
          />
          <Button
            variant="contained"
            size="small"
            className="cp-cta comment-save"
            sx={{ marginTop: 2, float: 'right', marginBottom: 2 }}
            onClick={handleSave}>
            <SaveIcon sx={{ fontSize: 18 }} />
            {translateWithUserLocale('save')}
          </Button>
        </Box>
      </StatefulModal>
    </>
  )

  return props.withPaper !== false ? (
    <Paper
      sx={{ display: 'flex', justifyContent: 'start' }}
      id="cp-creative-comment">
      {content}
    </Paper>
  ) : (
    content
  )
}

export default CommentComponent
